import React from 'react';

interface PreScreenResult {
  questionId: string;
  answer: string | number;
  questionType: 'numeric' | 'multiple-choice' | 'location' | 'text' | 'video';
  question: string;
  videoKey?: string;
  videoUrl?: string;
}

interface IPreScreenResults {
  preScreenResults: PreScreenResult[];
  loading: boolean;
  error: string | null;
}

export const PreScreenResults = (props: IPreScreenResults) => {
  const { preScreenResults, loading, error } = props;

  if (loading) {
    return (
      <div className='flex items-center justify-center min-h-[400px]'>
        <div className='flex flex-col items-center space-y-4'>
          <div className='w-8 h-8 border-3 border-indigo-200 border-t-indigo-600 rounded-full animate-spin'></div>
          <p className='text-sm font-medium text-slate-600'>
            Loading pre-screen results...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className='bg-gradient-to-r from-red-50 to-red-50/50 border border-red-200/60 rounded-xl p-6'>
        <div className='flex items-start space-x-4'>
          <div className='flex-shrink-0'>
            <div className='w-10 h-10 bg-red-100 rounded-full flex items-center justify-center'>
              <svg
                  className='w-5 h-5 text-red-600'
                  fill='none'
                  viewBox='0 0 24 24'
                  stroke='currentColor'
              >
                <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
                />
              </svg>
            </div>
          </div>
          <div className='flex-1'>
            <h3 className='text-lg font-semibold text-red-900 mb-1'>
              Unable to Load Results
            </h3>
            <p className='text-red-700 leading-relaxed'>{ error }</p>
          </div>
        </div>
      </div>
    );
  }

  if (!preScreenResults || preScreenResults.length === 0) {
    return (
      <div className='bg-gradient-to-br from-slate-50 to-slate-100/50 border border-slate-200/60 rounded-2xl p-12 text-center'>
        <div className='max-w-md mx-auto'>
          <div className='w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-6'>
            <svg
                className='w-8 h-8 text-slate-400'
                fill='none'
                viewBox='0 0 24 24'
                stroke='currentColor'
            >
              <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={1.5}
                  d='M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z'
              />
            </svg>
          </div>
          <h3 className='text-xl font-semibold text-slate-900 mb-3'>
            No Pre-Screen Questions
          </h3>
          <p className='text-slate-600 leading-relaxed'>
            This assessment doesn&apos;t include any pre-screening questions or
            the results are not yet available.
          </p>
        </div>
      </div>
    );
  }

  const getQuestionTypeColor = (type: string) => {
    switch (type) {
      case 'numeric':
        return 'bg-emerald-100 text-emerald-700 border-emerald-200';
      case 'multiple-choice':
        return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'video':
        return 'bg-purple-100 text-purple-700 border-purple-200';
      default:
        return 'bg-slate-100 text-slate-700 border-slate-200';
    }
  };

  const getQuestionTypeIcon = (type: string) => {
    switch (type) {
      case 'numeric':
        return (
          <svg
              className='w-4 h-4'
              fill='none'
              viewBox='0 0 24 24'
              stroke='currentColor'
          >
            <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M7 20l4-16m2 16l4-16M6 9h14M4 15h14'
            />
          </svg>
        );
      case 'multiple-choice':
        return (
          <svg
              className='w-4 h-4'
              fill='none'
              viewBox='0 0 24 24'
              stroke='currentColor'
          >
            <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z'
            />
          </svg>
        );
      case 'video':
        return (
          <svg
              className='w-4 h-4'
              fill='none'
              viewBox='0 0 24 24'
              stroke='currentColor'
          >
            <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z'
            />
          </svg>
        );
      default:
        return (
          <svg
              className='w-4 h-4'
              fill='none'
              viewBox='0 0 24 24'
              stroke='currentColor'
          >
            <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
            />
          </svg>
        );
    }
  };

  return (
    <div className='space-y-4'>
      { /* Header - Compact Version */ }
      <div className='bg-white border border-slate-200/60 rounded-xl shadow-sm overflow-hidden'>
        <div className='bg-gradient-to-r from-indigo-50/50 to-emerald-50/50 px-5 py-3 border-b border-slate-200/60'>
          <div className='flex items-center justify-between'>
            <div className='flex items-center gap-3'>
              <div className='w-8 h-8 bg-white rounded-lg shadow-sm flex items-center justify-center border border-slate-100'>
                <svg
                    className='w-4 h-4 text-indigo-600'
                    fill='none'
                    viewBox='0 0 24 24'
                    stroke='currentColor'
                >
                  <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d='M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4'
                  />
                </svg>
              </div>
              <h2 className='text-base font-bold text-slate-800 tracking-tight'>
                Pre-Screen Results
              </h2>
            </div>
            <div className='text-xs font-semibold px-2 py-0.5 bg-indigo-100/50 text-indigo-700 rounded-full border border-indigo-200/50'>
              { preScreenResults.length } Completed
            </div>
          </div>
        </div>

        { /* Results List - More compact layout */ }
        <div className='divide-y divide-slate-100'>
          { preScreenResults.map((result, index) => (
            <div
                key={result.questionId || index}
                className='p-4 hover:bg-slate-50/50 transition-colors group'
            >
              <div className='flex items-start gap-4'>
                { /* Compact index and type indicator */ }
                <div className='flex-shrink-0 pt-1'>
                  <div
                      className={`w-6 h-6 rounded-md flex items-center justify-center text-[10px] font-bold border ${getQuestionTypeColor(
                        result.questionType,
                      )}`}
                  >
                    { index + 1 }
                  </div>
                </div>

                <div className='flex-1 min-w-0'>
                  { /* Question and Type badge in one line */ }
                  <div className='flex flex-wrap items-center gap-2 mb-1'>
                    <span className='text-xs font-semibold text-slate-400 uppercase tracking-widest'>
                      Question
                    </span>
                    <div
                        className={`inline-flex items-center px-1.5 py-0.5 rounded border text-[9px] font-bold uppercase tracking-tight ${getQuestionTypeColor(
                          result.questionType,
                        )}`}
                    >
                      { result.questionType.replace('-', ' ') }
                    </div>
                  </div>

                  <h3 className='text-sm font-medium text-slate-800 leading-snug mb-3'>
                    { result.question }
                  </h3>

                  { /* Horizontal Response box */ }
                  <div className='flex items-center gap-3 bg-slate-50/80 rounded-lg p-2.5 border border-slate-100/50 group-hover:bg-white transition-colors group-hover:border-slate-200/80'>
                    <span className='text-[10px] font-bold text-slate-400 uppercase tracking-widest'>
                      Answer
                    </span>
                    <div className='flex-1'>
                      { result.questionType === 'numeric' ? (
                        <span className='text-sm font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-100/50'>
                          { result.answer }
                        </span>
                      ) : result.questionType === 'video' ? (
                        result.videoUrl ||
                        (typeof result.answer === 'string' &&
                          result.answer.startsWith('http')) ? (
                            <video
                                src={result.videoUrl || String(result.answer)}
                                preload='metadata'
                                controls
                                className='w-full max-w-sm rounded-xl border border-slate-200 bg-slate-950'
                            />
                          ) : (
                            <span className='inline-flex items-center gap-2 text-sm font-semibold text-purple-700 bg-purple-50 px-2 py-0.5 rounded border border-purple-100/50'>
                              { getQuestionTypeIcon('video') }
                              Video submitted
                            </span>
                          )
                      ) : (
                        <span className='text-sm font-semibold text-sky-700 bg-sky-50 px-2 py-0.5 rounded border border-sky-100/50'>
                          { result.answer }
                        </span>
                      ) }
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )) }
        </div>
      </div>
    </div>
  );
};
